import cx from "classnames";

import { Optional } from "common/utils/types";
import LinkButton, { LinkButtonProps } from "common/components/LinkButton";
import Angle from "common/components/Angle";
import Spinner from "common/components/Spinner";

import styles from "../../styles/Banner.module.less";

export type BannerVariant =
  | "primary"
  | "promotion"
  | "promotion-dark"
  | "danger"
  | "success"
  | "warning"
  | "info";

type BannerProps = {
  children: React.ReactNode;
  inline?: boolean;
  variant?: BannerVariant;
  className?: string;
};

const Banner = ({ variant = "primary", inline, className, children }: BannerProps) => {
  return (
    <div
      className={cx(
        styles.container,
        styles[`variant-${variant}`],
        inline && styles.inline,
        className,
      )}
    >
      {children}
    </div>
  );
};

type BannerContentProps = {
  label: React.ReactNode;
  description?: Optional<React.ReactNode>;
};

const BannerContent = ({ label, description }: BannerContentProps) => {
  return (
    <div className={styles.content}>
      <div className={styles.label}>{label}</div>
      {description && <div className={styles.description}>{description}</div>}
    </div>
  );
};

type BannerTextProps = {
  label: string;
  align?: "center" | "left";
  actions?: (LinkButtonProps & { children: string })[];
  /** Disables every action behind one spinner, e.g. while what they'd fix is re-checked */
  areActionsLoading?: boolean;
};

const BannerText = ({
  label,
  align = "center",
  actions = [],
  areActionsLoading = false,
}: BannerTextProps) => {
  return (
    <div className={cx(styles.text, styles[align], actions.length > 1 && styles.multiple)}>
      <span>{label}</span>
      {actions.length > 0 && (
        <div className={styles.actions}>
          {areActionsLoading && <Spinner />}
          {actions.map((action) => (
            <LinkButton
              key={action.children}
              {...action}
              disabled={action.disabled || areActionsLoading}
              className={styles.action}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type BannerLinkButtonProps = BannerProps & LinkButtonProps;

const BannerLinkButton = ({
  variant = "primary",
  inline,
  className,
  children,
  ...other
}: BannerLinkButtonProps) => {
  return (
    <LinkButton
      className={cx(
        styles.container,
        className,
        styles[`variant-${variant}`],
        inline && styles.inline,
        styles.clickable,
      )}
      {...other}
    >
      {children}
      <Angle direction="right" className={styles.icon} />
    </LinkButton>
  );
};

export default Object.assign(Banner, {
  LinkButton: BannerLinkButton,
  Content: BannerContent,
  Text: BannerText,
});
